import React, {
  useEffect, useRef, useState, useCallback,
} from 'react';
import ReactMarkdown from 'react-markdown';
import { useQnAContext } from './contexts/QnAContext';
import styles from './QnAWidget.module.scss';

export function QnAChatArea() {
  const { messages } = useQnAContext();
  const chatEndRef = useRef<HTMLDivElement>(null);
  const chatAreaRef = useRef<HTMLDivElement>(null);
  const [shouldAutoScroll, setShouldAutoScroll] = useState(true);
  const [isUserScrolling, setIsUserScrolling] = useState(false);

  const checkScrollPosition = useCallback(() => {
    if (!chatAreaRef.current) return;

    const { scrollTop, scrollHeight, clientHeight } = chatAreaRef.current;
    const isAtBottom = scrollHeight - scrollTop - clientHeight < 50;

    setShouldAutoScroll(isAtBottom);
  }, []);

  const handleScroll = useCallback(() => {
    if (!isUserScrolling) {
      setIsUserScrolling(true);
      setTimeout(() => setIsUserScrolling(false), 150);
    }
    checkScrollPosition();
  }, [checkScrollPosition, isUserScrolling]);

  useEffect(() => {
    if (shouldAutoScroll && !isUserScrolling) {
      chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, shouldAutoScroll, isUserScrolling]);

  useEffect(() => {
    const chatArea = chatAreaRef.current;
    if (chatArea) {
      chatArea.addEventListener('scroll', handleScroll, { passive: true });
      return () => {
        chatArea.removeEventListener('scroll', handleScroll);
      };
    }
    return undefined;
  }, [handleScroll]);

  const formatTime = (timestamp: Date) => timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

  const getMessageClassName = (type: string) => {
    switch (type) {
      case 'user':
        return `${styles.message} ${styles.messageUser}`;
      case 'ai':
        return `${styles.message} ${styles.messageAi}`;
      case 'system':
        return `${styles.message} ${styles.messageSystem}`;
      default:
        return styles.message;
    }
  };

  const renderMessageContent = (message: any) => {
    if (message.type === 'ai') {
      return (
        <ReactMarkdown className={styles.markdownContent}>
          {message.content}
        </ReactMarkdown>
      );
    }

    return message.content;
  };

  return (
    <div ref={chatAreaRef} className={styles.chatArea}>
      {messages.length === 0 && (
        <div className={`${styles.message} ${styles.messageSystem}`}>
          <div className={styles.messageContent}>
            Welcome to QnA Assistant! Ask me anything about the platform.
          </div>
        </div>
      )}

      {messages.map((message) => (
        <div key={message.id} className={getMessageClassName(message.type)}>
          <div className={styles.messageContent}>
            {renderMessageContent(message)}
          </div>
          <div className={styles.messageTime}>
            {formatTime(message.timestamp)}
          </div>
        </div>
      ))}

      <div ref={chatEndRef} />
    </div>
  );
}
